import { AppDispatch } from '../../../shared/state/store';
import { emptyPatientNote } from '@finpay-development/shared-platform/lib/esm/patient/components/models/patient-note';
import { createCommentSuccesMessages } from '@finpay-development/shared-platform/lib/esm/shared/enums';
import { createPatientNote, putProgram, savePatientPaymentProgram } from '@finpay-development/shared-platform/lib/esm/patient/state/patient-thunk';
import { makePayment } from '@finpay-development/shared-platform/lib/esm/admissions-advisor/state/vob-patient-thunk';
import { setEstimate } from '@finpay-development/shared-platform/lib/esm/admissions-advisor/state/estimator-slice';
import { setSelectedEncounter } from '@finpay-development/shared-platform/lib/esm/patient/state/patient-slice';
import { showStatus } from '@finpay-development/shared-platform/lib/esm/security/state/user-slice';
import { Utils } from '@finpay-development/shared-platform/lib/esm/shared/utils';
import { Estimate } from '@finpay-estimation/estimation-types';
import { patientService } from '@finpay-development/shared-platform/lib/esm/patient/services/patient-service';
import { processUserRecord } from '../../../shared/payment/user-record-utils';
import { admissionsAdvisorUtils } from '@finpay-development/shared-platform/lib/esm/admissions-advisor/utils/admission-advisor-utils';
import { ClientCrm } from '@finpay-development/shared-platform/lib/esm/shared/model/client-status-card';
import { PatientPaymentProgram } from '@finpay-development/shared-platform/lib/esm/patient/components/models/patient-payment-program';
import { Payment } from '@finpay-development/shared-platform/lib/esm/patient/components/models/payment';
import { PAYMENT_TYPES } from '@finpay-development/shared-platform/lib/esm/patient/components/models/payment-type';

/**
 * Helper functions for patient record operations
 */
export const patientRecordUtils = {
  /**
   * Creates patient notes based on form values
   */
  createNotes: async (
    dispatch: AppDispatch,
    formValues: any,
    selectedIOC: any,
    selectedPatient: any
  ) => {
    let comment = { ...emptyPatientNote };
    const currentDate = new Date();

    comment.completionDueDt = '';
    comment.patientEncounterId = selectedIOC.patientEncounterId;
    comment.patientId = selectedPatient?.patientId;
    comment.noteDt = currentDate.toISOString();
    comment.isReminder = false;

    const interactionType = formValues.isWarmTransfer || '';
    const noteText = formValues.noteText || '';
    const hasNoteText = noteText.trim().length > 0;
    const hasInteractionType = interactionType.trim().length > 0;
    
        let formedNotes = '';
    if (hasInteractionType && hasNoteText) {
      formedNotes = interactionType.concat(' - ', noteText);
    } else if (hasInteractionType) {
      formedNotes = interactionType;
    } else if (hasNoteText) {
      formedNotes = noteText;
    }

    if (formValues.isPayAtFacility && Number(formValues.payAtFacilityAmt) > 0) {
      const paidAtFacilityNote = `$${Number(formValues.payAtFacilityAmt)} to be taken at facility`;
      if (formedNotes.length > 0) {
        formedNotes = formedNotes.concat(' - ', paidAtFacilityNote);
      } else {
        formedNotes = paidAtFacilityNote;
      }
    }

    comment.noteText = formedNotes;

    return dispatch(createPatientNote({
      message: createCommentSuccesMessages.comment,
      comment,
      showNotify: false
    })).unwrap();
  },

  /**
   * Handles payment processing
   */
  handlePayment: async (
    dispatch: AppDispatch,
    formValues: any,
    selectedIOC: any,
    selectedPatient: any,
    clientCrm: ClientCrm[],
    stripeToken: string,
    channelId: number,
    handlePatientRecordModalCancel: () => void,
    setOwnerId: (userId: string | null) => void,
    iocData: any = null
  ) => {
    let userRecord: any;
    let userId: string | null = null;
    
    // Process user record if email provided (use receiptEmail or fallback to email)
    const emailForUserRecord = formValues.receiptEmail || formValues.email;
    if (emailForUserRecord) {
      userRecord = await processUserRecord(
        emailForUserRecord,
        false, // not in patient portal
        handlePatientRecordModalCancel,
        {
          userEmail: emailForUserRecord,
          firstName: formValues.patientFirstName,
          lastName: formValues.patientLastName,
          clientId: selectedIOC?.clientId,
          patientEncounterId: selectedIOC.patientEncounterId,
          dispatch
        },
        (newUserId) => {
          userId = newUserId;
          setOwnerId(newUserId);
        }
      );
      
      // If user processing failed, exit early
      if (!userRecord) {
        return false;
      }
    }
    
    // Make payment
    const totalPaymentAmount = Number(formValues.payNowAmt);
    const paymentInfo = await patientRecordUtils.makePreConvertedPayment(
      totalPaymentAmount,
      selectedIOC.patientEncounterId,
      selectedPatient.patientId,
      formValues,
      stripeToken,
      channelId,
      clientCrm,
      dispatch,
      userId,
      iocData || selectedIOC
    );
    
    await dispatch(makePayment({payment: paymentInfo, showNotify: false})).unwrap();
    
    // Update program if needed
    if (selectedIOC?.patientPaymentProgram?.[0]?.patientPaymentProgramId) {
      await dispatch(putProgram({
        patientPaymentProgramId: selectedIOC.patientPaymentProgram[0].patientPaymentProgramId,
        paymentProgram: {},
        updateState: true,
      }));
    }
    
    // Call RT update AFTER payment is complete (moved from makePreConvertedPayment)
    const isIntegrationEnabled = admissionsAdvisorUtils.checkIntegrationFeatureFlag(clientCrm);
    if (isIntegrationEnabled) {
      await patientService.integrationUpdate({
        patientEncounterId: selectedIOC.patientEncounterId,
        crmTypeSlug: clientCrm.length > 0 ? clientCrm[0].crmType.crmTypeSlug : undefined,
        patientId: selectedPatient?.patientId,
      });
    }
    
    return true;
  },

  /**
   * Creates a payment object for pre-converted payments
   */
  makePreConvertedPayment: async (
    totalPaymentAmount: number,
    encounterId: number,
    patientId: number,
    formValues: any,
    stripeToken: string,
    channelId: number,
    clientCrm: ClientCrm[],
    dispatch: AppDispatch,
    ownerId: string | null,
    selectedIOC: any
  ) => {
    // Check if there's an existing payment program
    let scheduleId: number;
    let selectedPatientEncounter: any;
    
    if (selectedIOC?.patientPaymentProgram?.[0]?.patientPaymentSchedule?.patientPaymentScheduleId) {
      // Use existing payment program
      scheduleId = selectedIOC.patientPaymentProgram[0].patientPaymentSchedule.patientPaymentScheduleId;
      selectedPatientEncounter = selectedIOC;
    } else {
      // Create a new payment program if one doesn't exist
      const paymentProgram = patientRecordUtils.mapToPatientPaymentProgram(formValues, selectedIOC, null);
      const paymentProgramResponse = await dispatch(savePatientPaymentProgram(
        {
          paymentProgram: paymentProgram,
          patientId: patientId,
          encounterId: encounterId,
          showNotify: false,
        }
      ));
      
      selectedPatientEncounter = (paymentProgramResponse.payload as any).selectedPatientEncounter;
      scheduleId = selectedPatientEncounter.patientPaymentProgram[0].patientPaymentSchedule.patientPaymentScheduleId;
    }
    
    const paymentInfo = patientRecordUtils.mapToPayment(
      totalPaymentAmount,
      encounterId,
      patientId,
      scheduleId,
      formValues,
      stripeToken,
      channelId,
      ownerId
    );

    return paymentInfo;
  },

  /**
   * Maps form values to a PatientPaymentProgram object
   */
  mapToPatientPaymentProgram: (formValues: any, selectedIOC?: any, estimatorState?: any) => {
    // Get PFR amount from estimator state if available
    let pfrAmt = 0;
    if (estimatorState?.summary) {
      const pfrSummary: any = estimatorState.summary;
      pfrAmt = pfrSummary.totalPFR;
    }
    
    const currentDate = new Date();
    const downPaymentAmt = 0;
    const terms = 0;
    const recurringPaymentAmt = 0;

    let patientPaymentSchedule: any;

    // If there's an existing payment schedule, use its values
    if (selectedIOC?.patientPaymentProgram?.[0]?.patientPaymentSchedule) {
      const {
        downPmtAmt,
        paymentDueAmt,
        paymentFreq,
        pfrAmt: existingPfrAmt,
        remainingTerms,
        scheduleStartDt,
        scheduleStatus,
        terms: existingTerms
      } = selectedIOC.patientPaymentProgram[0].patientPaymentSchedule;
  
      patientPaymentSchedule = {
        downPmtAmt,
        paymentDueAmt,
        paymentFreq,
        pfrAmt: existingPfrAmt || pfrAmt,
        remainingTerms,
        scheduleStartDt,
        scheduleStatus,
        terms: existingTerms
      };
    } else {
      // Otherwise create a new payment schedule
      patientPaymentSchedule = {
        pfrAmt: pfrAmt,
        pfrBalance: pfrAmt,
        paymentFreq: 'M',
        downPmtAmt: downPaymentAmt,
        terms: terms,
        remainingTerms: terms,
        paymentDueAmt: recurringPaymentAmt,
        scheduleStatus: 'Pending',
        scheduleStartDt: currentDate.toISOString(),
      };
    }

    // Create payment program
    const paymentProgram = {
      patientPaymentProgramId: selectedIOC?.patientPaymentProgram?.[0]?.patientPaymentProgramId || 0,
      isHighRisk: false,
      isPaidInFull: false,
      downPmtAmt: downPaymentAmt,
      isACH: false,
      areDocsSigned: false,
      patientPaymentSchedule: patientPaymentSchedule,
    } as PatientPaymentProgram;
    
    return paymentProgram;
  },

  /**
   * Maps payment information to a Payment object
   */
  mapToPayment: (
    totalPaymentAmt: number,
    patientEncounterId: number,
    patientId: number,
    scheduleId: number,
    formValues: any,
    stripeToken: string,
    channelId: number,
    ownerId: string | null
  ) => {
    const currentDateTime = new Date();
    return {
      isAuthExpired: false, 
      isAuthOnly: false, 
      isManual: true,
      patientEncounterId: patientEncounterId,
      patientId: patientId,
      patientPaymentScheduleId: scheduleId,
      paymentAmt: totalPaymentAmt,
      paymentGroupId: scheduleId.toString(),
      paymentId: 0, 
      paymentInitDt: currentDateTime.toISOString(),
      paymentMethods: [{
        externalPaymentMethodId: 'card',
        paymentMethodTypeId: 1,
        externalPaymentId: stripeToken,
        payorId: '',
        nameOnCard: formValues.nameOnCard
      }],
      paymentType: PAYMENT_TYPES.DOWN_PAYMENT,
      receiptEmail: formValues.receiptEmail || formValues.email,
      paymentChannelId: channelId,
      isFinAdvisor: true,
      ...(formValues.paymentReason ? { paymentReason: formValues.paymentReason } : {}),
      ...(ownerId ? { ownerId: Number(ownerId) } : {}),
    } as Payment;
  },

  /**
   * Handles integration updates
   */
  handleIntegrationUpdates: async (
    selectedIOC: any,
    clientCrm: ClientCrm[],
    selectedPatient: any
  ) => {
    const isIntegrationEnabled = admissionsAdvisorUtils.checkIntegrationFeatureFlag(clientCrm);
    
    if (isIntegrationEnabled) {
      await patientService.integrationUpdate({
        patientEncounterId: selectedIOC.patientEncounterId,
        crmTypeSlug: clientCrm.length > 0 ? clientCrm[0].crmType.crmTypeSlug : undefined,
        patientId: selectedPatient?.patientId,
      });
    }
  },

  /**
   * Updates payment program
   */
  updatePaymentProgram: async (
    dispatch: AppDispatch,
    selectedIOC: any
  ) => {
    const currentPaymentProgramId = selectedIOC?.patientPaymentProgram?.[0]?.patientPaymentProgramId;
    if (currentPaymentProgramId) {
      await dispatch(putProgram({
        patientPaymentProgramId: currentPaymentProgramId,
        paymentProgram: {},
        updateState: true,
      }));
    }
  },

  /**
   * Updates estimator with patient encounter ID
   */
  updateEstimator: (
    dispatch: AppDispatch,
    estimatorState: Estimate,
    selectedIOC: any,
  ) => {
    let estimatorStateCopy = Utils.deepClone(estimatorState);
    estimatorStateCopy.patientEncounterId = selectedIOC.patientEncounterId;
    estimatorStateCopy = patientRecordUtils.setEstimatorDescription(estimatorStateCopy);
    dispatch(setEstimate(estimatorStateCopy));
    return estimatorStateCopy;
  },

  /**
   * Sets estimator description
   */
  setEstimatorDescription: (estimatorBody: Estimate) => {
    if (!estimatorBody.description || estimatorBody.description === "") {
      // Use current date if no patient name is available
      estimatorBody.description = `Estimate - ${(new Date()).toLocaleDateString('en-US', {timeZone: "UTC"})}`
    }
    return estimatorBody;
  }
}; 